import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'
import Lightbox from '@shared/components/Lightbox'
import type { LightboxItem } from '@shared/components/Lightbox'
import WorkflowStages from '@shared/components/WorkflowStages'

const POSTERS = [
    '/uploads/2026/10/Call_For_Papers_ISR_Vol7_No_II_2026_page-0001.jpg',
    '/uploads/2026/10/Call_For_Papers_ISR_Vol7_No_II_2026_page-0002.jpg'
]

const SESSION_IMAGES: { date: string; src: string }[] = [
    { date: 'Vol. 7 · Issue 1', src: '/uploads/2026/03/Call_For_Papers_ISR_Vol7_No_I_2026-1_page-0001-scaled.jpg' },
    { date: '15 Oct 2025',      src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.04-PM-1.jpeg' },
    { date: '15 April 2025',    src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.02-PM-3.jpeg' },
    { date: 'Session 2024',     src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.04-PM-3.jpeg' },
    { date: '13 May 2024',      src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.03-PM-1.jpeg' },
    { date: '30 Nov 2023',      src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.04-PM-2.jpeg' },
    { date: '30 May 2023',      src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.03-PM-2.jpeg' },
    { date: '25 November 2022', src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.03-PM.jpeg' },
    { date: '31 May 2022',      src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.02-PM.jpeg' },
    { date: '30 Nov 2021',      src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.02-PM-1.jpeg' },
    { date: '27 July 2021',     src: '/uploads/2026/02/WhatsApp-Image-2026-02-10-at-5.20.02-PM-2.jpeg' }
]

const CYCLE = {
    volume: 'Vol. 7 · Issue 2',
    period: 'July – December 2026'
}

const THEMES = [
    'Federalism & inter-governmental relations',
    'Electoral institutions & political behaviour',
    'Public-policy design & implementation',
    'Judicial review & constitutional practice',
    'Local government & urban politics',
    'Foreign policy & strategic affairs',
    'Political economy & development',
    'Civil society, media & democratic accountability'
]

const REQUIREMENTS = [
    'Original, unpublished research articles of 5,000–6,000 words including references and appendices (max. 6 pages in the 2-column template). Book reviews 1,200–1,500 words.',
    'Abstract of 200–250 words plus up to 5 keywords.',
    'Prepared in the official Manuscript Title Template and 2-column Manuscript Template.',
    'Separate title page with author details and affiliation; fully anonymised manuscript in PDF or DOCX (≤ 10 MB).',
    'APA style (author–date) references, around 15–20 per article; tables and figures numbered and labelled.',
    'Mandatory plagiarism report with similarity ≤ 10% (UGC Regulations, 2018). Any use of AI tools must be disclosed.'
]

export default function CallForPapers() {
    const gallery: LightboxItem[] = [
        ...POSTERS.map((src, i) => ({
            kind: 'image' as const,
            src,
            caption: `${CYCLE.volume} — page ${i + 1}`
        })),
        ...SESSION_IMAGES.map((s) => ({
            kind: 'image' as const,
            src: s.src,
            caption: `Call for Papers · ${s.date}`
        }))
    ]
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    return (
        <>
            <SEO
                title="Call for Papers"
                description={`${CYCLE.volume}. Submissions open year-round — 365 days, 24×7, no deadline. Peer-reviewed bi-annual journal, double-blind review, timely peer review.`}
                path="/call-for-papers"
            />
            <PageHeader
                eyebrow="Open Submissions"
                title="Call for Papers"
                lede={`${CYCLE.volume} · Submissions open year-round — 365 days, 24×7.`}
            />

            <section
                className="section"
                style={{
                    background: 'var(--color-gold-800)',
                    color: 'var(--color-paper-100)',
                    borderBottom: '1px solid var(--color-rule)'
                }}
            >
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="grid md:grid-cols-12 gap-8 items-center"
                    >
                        <div className="md:col-span-7">
                            <div
                                className="eyebrow eyebrow--light"
                                style={{ color: 'var(--color-gold-300)' }}
                            >
                                Submissions open · year-round
                            </div>
                            <h2
                                className="font-display mt-4"
                                style={{
                                    fontSize: 'clamp(2rem, 4vw, 3rem)',
                                    color: 'var(--color-paper-100)',
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.05
                                }}
                            >
                                Open <em style={{ fontStyle: 'italic', color: 'var(--color-gold-300)' }}>365 days</em> a year
                            </h2>
                            <p
                                className="mt-4 text-[15px] leading-relaxed"
                                style={{ color: 'rgba(243,236,217,0.7)', maxWidth: '34rem' }}
                            >
                                We invite original research articles, review essays, and policy
                                analyses for <em>Indian Studies Review</em> {CYCLE.volume}
                                {CYCLE.period ? ` (${CYCLE.period})` : ''}. There is no submission
                                deadline — manuscripts are accepted around the clock and double-blind
                                peer-reviewed in a timely manner.
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <Link to="/submission" className="btn btn-gold">
                                    Submit a manuscript →
                                </Link>
                                <Link to="/guidelines" className="btn btn-outline-light">
                                    Read submission guidelines
                                </Link>
                            </div>
                        </div>

                        <div className="md:col-span-5">
                            <div
                                className="p-7"
                                style={{
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderTop: '2px solid var(--color-gold-500)'
                                }}
                            >
                                <div
                                    className="text-[10.5px] uppercase font-semibold"
                                    style={{ letterSpacing: '0.24em', color: 'var(--color-gold-300)' }}
                                >
                                    Submission window
                                </div>
                                <div className="mt-5 grid grid-cols-2 gap-6">
                                    <CountdownCell value="365" label="Days a year" />
                                    <CountdownCell value="24×7" label="Around the clock" />
                                </div>
                                <div
                                    className="mt-5 text-[12px]"
                                    style={{ color: 'rgba(243,236,217,0.55)' }}
                                >
                                    No deadline · rolling submissions · open access
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="section">
                <div className="container-prose grid md:grid-cols-12 gap-12">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="md:col-span-5"
                    >
                        <div className="eyebrow">Scope</div>
                        <h2 className="section-title mt-5">
                            Submissions we welcome
                        </h2>
                        <p className="prose-body mt-6">
                            We look for work with a clear theoretical frame and sound methodology on
                            Indian polity, economy and society. Review articles, policy analyses and
                            book reviews are equally welcome, as is comparative work that places India
                            in conversation with other democracies.
                        </p>
                        <p className="text-[13px] italic mt-4" style={{ color: 'var(--color-ink-400)' }}>
                            The list is indicative, not exhaustive.
                        </p>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="md:col-span-7 grid sm:grid-cols-2 gap-x-8"
                        style={{ borderTop: '1px solid var(--color-rule)' }}
                    >
                        {THEMES.map((t, i) => (
                            <li
                                key={t}
                                className="grid grid-cols-[1.75rem_1fr] gap-3 items-baseline py-4"
                                style={{ borderBottom: '1px solid var(--color-rule)' }}
                            >
                                <span
                                    className="font-display"
                                    style={{
                                        fontSize: '0.85rem',
                                        color: 'var(--color-gold-700)',
                                        letterSpacing: '0.04em'
                                    }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <span className="text-[14.5px]" style={{ color: 'var(--color-ink-600)' }}>
                                    {t}
                                </span>
                            </li>
                        ))}
                    </motion.ul>
                </div>
            </section>

            <section
                className="section paper-grain"
                style={{
                    borderTop: '1px solid var(--color-rule)',
                    borderBottom: '1px solid var(--color-rule)'
                }}
            >
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-center"
                    >
                        <div className="eyebrow eyebrow--center">Editorial Calendar</div>
                        <h2 className="section-title mt-5">
                            What happens after you submit
                        </h2>
                        <p className="lede mt-6 mx-auto" style={{ maxWidth: '40rem' }}>
                            Every manuscript moves through eleven transparent stages. Authors hear back at
                            every step by email and can track the live status in the editorial portal.
                        </p>
                    </motion.div>

                    <div className="mt-14">
                        <WorkflowStages />
                    </div>
                </div>
            </section>

            <section className="section">
                <div className="container-prose grid md:grid-cols-12 gap-12">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="md:col-span-5"
                    >
                        <div className="eyebrow">Requirements</div>
                        <h2 className="section-title mt-5">
                            What we need from you
                        </h2>
                        <Link to="/guidelines" className="link-arrow mt-6 inline-flex">
                            Full submission guidelines →
                        </Link>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="md:col-span-7"
                    >
                        {REQUIREMENTS.map((r, i) => (
                            <li
                                key={r}
                                className="py-4 grid grid-cols-[2rem_1fr] gap-3 items-baseline"
                                style={{ borderBottom: '1px solid var(--color-rule)' }}
                            >
                                <span
                                    className="font-display italic"
                                    style={{
                                        fontSize: '1rem',
                                        color: 'var(--color-gold-700)'
                                    }}
                                >
                                    {i + 1}.
                                </span>
                                <span className="text-[15px] leading-relaxed" style={{ color: 'var(--color-ink-500)' }}>
                                    {r}
                                </span>
                            </li>
                        ))}
                    </motion.ul>
                </div>
            </section>

            <section
                className="section"
                style={{
                    background: 'var(--color-paper-100)',
                    borderTop: '1px solid var(--color-rule)',
                    borderBottom: '1px solid var(--color-rule)'
                }}
            >
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-center"
                    >
                        <div className="eyebrow eyebrow--center">Official Posters</div>
                        <h2 className="section-title mt-5">
                            {CYCLE.volume} — full announcement
                        </h2>
                        <p
                            className="text-[13.5px] mt-4 max-w-md mx-auto"
                            style={{ color: 'var(--color-ink-400)' }}
                        >
                            Click any page to open the in-page reader with zoom, navigation,
                            and download — no new tab needed.
                        </p>
                    </motion.div>
                    <div className="mt-12 grid sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
                        {POSTERS.map((src, i) => (
                            <motion.button
                                key={src}
                                type="button"
                                onClick={() => setOpenIndex(i)}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45, delay: i * 0.08 }}
                                className="block group text-left"
                            >
                                <div
                                    className="aspect-[3/4] overflow-hidden relative"
                                    style={{
                                        background: '#fff',
                                        border: '1px solid var(--color-rule-strong)',
                                        boxShadow: '0 18px 48px -24px rgba(13,27,42,0.22)'
                                    }}
                                >
                                    <img
                                        src={src}
                                        alt={`Call for Papers poster ${i + 1}`}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                    />
                                    <div
                                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{ background: 'rgba(13,11,8,0.32)' }}
                                    >
                                        <span
                                            className="inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase font-semibold"
                                            style={{
                                                background: 'var(--color-gold-500)',
                                                color: 'var(--color-navy-900)',
                                                letterSpacing: '0.18em'
                                            }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="11" cy="11" r="8" />
                                                <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                                            </svg>
                                            Open reader
                                        </span>
                                    </div>
                                </div>
                                <div
                                    className="mt-3 text-center text-[11px] uppercase font-semibold"
                                    style={{ letterSpacing: '0.2em', color: 'var(--color-gold-700)' }}
                                >
                                    Page {i + 1}
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section">
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-center"
                    >
                        <div className="eyebrow eyebrow--center">Session Archive</div>
                        <h2 className="section-title mt-5">
                            Every Call for Papers since <em>2021</em>
                        </h2>
                        <p
                            className="text-[13.5px] mt-4 max-w-md mx-auto"
                            style={{ color: 'var(--color-ink-400)' }}
                        >
                            All historic poster announcements. Click any to read in the same window.
                        </p>
                    </motion.div>

                    <ul
                        className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5"
                    >
                        {SESSION_IMAGES.map((s, i) => {
                            const galleryIndex = POSTERS.length + i
                            return (
                                <motion.li
                                    key={s.src}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: (i % 5) * 0.04 }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setOpenIndex(galleryIndex)}
                                        className="block w-full text-left group"
                                    >
                                        <div
                                            className="aspect-[3/4] overflow-hidden relative"
                                            style={{
                                                background: 'var(--color-paper-100)',
                                                border: '1px solid var(--color-rule-strong)'
                                            }}
                                        >
                                            <img
                                                src={s.src}
                                                alt={`Call for Papers — ${s.date}`}
                                                loading="lazy"
                                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                                            />
                                            <div
                                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                style={{
                                                    background:
                                                        'linear-gradient(180deg, transparent 50%, rgba(13,11,8,0.72) 100%)'
                                                }}
                                            />
                                        </div>
                                        <div
                                            className="mt-2 text-[10.5px] uppercase font-semibold"
                                            style={{
                                                letterSpacing: '0.18em',
                                                color: 'var(--color-ink-500)'
                                            }}
                                        >
                                            {s.date}
                                        </div>
                                    </button>
                                </motion.li>
                            )
                        })}
                    </ul>
                </div>
            </section>

            <section className="section">
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mx-auto text-center"
                        style={{ maxWidth: '42rem' }}
                    >
                        <div className="eyebrow eyebrow--center">Ready to submit</div>
                        <h2 className="section-title mt-5">
                            Send us your <em>best</em> work
                        </h2>
                        <p className="lede mt-6">
                            Author registration takes a minute. The portal walks you through manuscript
                            upload, co-authors, and the cover letter step by step.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-3 justify-center">
                            <Link to="/submission" className="btn btn-primary">
                                Begin submission
                            </Link>
                            <Link to="/contact-us" className="btn btn-ghost">
                                Ask the editor a question
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            <Lightbox
                items={gallery}
                openIndex={openIndex}
                onClose={() => setOpenIndex(null)}
                onIndexChange={setOpenIndex}
            />
        </>
    )
}

function CountdownCell({ value, label }: { value: string | number; label: string }) {
    return (
        <div>
            <div
                className="font-display numerals"
                style={{
                    fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                    lineHeight: 1,
                    color: 'var(--color-paper-100)',
                    letterSpacing: '-0.025em'
                }}
            >
                {value}
            </div>
            <div
                className="mt-2 text-[10.5px] uppercase font-semibold"
                style={{ letterSpacing: '0.22em', color: 'rgba(243,236,217,0.6)' }}
            >
                {label}
            </div>
        </div>
    )
}
